import React, { CSSProperties, MouseEventHandler, useEffect } from "react"
import styles from "./SidebarRight.module.scss"
import cn from "classnames"
import { ClassProps } from "../../utility/common/props"
import CloseButton from "../CloseButton/CloseButton"
import SettingsContextMenu from "../SettingsContextMenu/SettingsContextMenu"
import SettingsButton from "../SettingsButton/SettingsButton"
import { selectCurrentProject } from "../../store/projects/selectors"
import { useAppSelector } from "../../store/hooks"
import IconControl from "../IconControl/IconControl"
import { faLongArrowLeft } from "@fortawesome/pro-light-svg-icons/faLongArrowLeft"
import TooltipTrigger from "../TooltipTrigger/TooltipTrigger"
import truncate from "lodash-es/truncate"
import { testId } from "../../utility/tests/testId/common"

const tooltipDelay = { show: 450, hide: 0 }
const SIDEBAR_TITLE_TRUNCATION_LIMIT = 40

export interface SidebarRightProps extends ClassProps {
    open: boolean
    title: string
    onClose: () => void
    onCloseClick?: () => void
    settings?: JSX.Element[]
    width?: number
    zIndex?: number
    onBack?: MouseEventHandler<HTMLDivElement>
    classNameClosed?: string
    contentClassName?: string
    isCloseButtonVisible?: boolean
    action?: JSX.Element
    withTitleSeparator?: boolean
    titleTruncationLimit?: number
}

const SidebarRight: React.FC<SidebarRightProps> = props => {
    const {
        children,
        onClose,
        onCloseClick,
        open,
        className,
        title,
        settings,
        width,
        zIndex,
        onBack,
        classNameClosed,
        contentClassName,
        isCloseButtonVisible = true,
        action,
        withTitleSeparator,
        titleTruncationLimit = SIDEBAR_TITLE_TRUNCATION_LIMIT
    } = props
    const currentProject = useAppSelector(selectCurrentProject)
    const isTitleLong = titleTruncationLimit > 0 && title.length > titleTruncationLimit
    const shortTitle = isTitleLong ? truncate(title, { length: titleTruncationLimit, omission: "..." }) : title

    const addCustomStyle = ({
        width,
        zIndex,
        isClosed
    }: {
        width?: number
        zIndex?: number
        isClosed: boolean
    }): CSSProperties => {
        let style: CSSProperties = {}
        if (width) {
            style = { width: `${width}px`, ...style }

            if (isClosed) {
                style = { marginRight: `-${width}px`, ...style }
            }
        }

        if (zIndex) {
            style = { zIndex, ...style }
        }

        return style
    }

    useEffect(() => {
        currentProject && onClose()
    }, [currentProject])

    return (
        <div
            className={cn(
                styles.sidebarRight,
                className,
                !open && styles.sidebarRight_closed,
                !open && classNameClosed
            )}
            style={width || zIndex ? addCustomStyle({ width, zIndex, isClosed: !open }) : undefined}
        >
            {
                <div className={styles.sidebarRight__header}>
                    {onBack ? (
                        <IconControl
                            icon={faLongArrowLeft}
                            onClick={onBack}
                            className={styles.sidebarRight__icon}
                            testId={testId.toPreviousPage}
                        />
                    ) : (
                        <CloseButton
                            onClick={onCloseClick ?? onClose}
                            className={styles.sidebarRight__icon}
                            testId={testId.btnClose}
                        />
                    )}
                    <TooltipTrigger
                        id="sidebar-right-title-tooltip"
                        placement="bottom"
                        content={title}
                        condition={isTitleLong}
                        delay={tooltipDelay}
                    >
                        <div className={styles.sidebarRight__title}>{shortTitle}</div>
                    </TooltipTrigger>
                    {settings && (
                        <div className={styles.sidebarRight__settings}>
                            <SettingsContextMenu items={settings}>
                                <SettingsButton />
                            </SettingsContextMenu>
                        </div>
                    )}
                    {action && (
                        <div className={styles.sidebarRight__buttonsContainer} data-testid={testId.sidebarActionButton}>
                            <div className={styles.sidebarRight__actionBtnRight}>{action}</div>
                            {onBack && isCloseButtonVisible && (
                                <CloseButton
                                    onClick={onCloseClick ?? onClose}
                                    className={cn(styles.sidebarRight__icon, styles.sidebarRight__icon_right)}
                                    testId={testId.btnClose}
                                />
                            )}
                        </div>
                    )}
                </div>
            }
            {withTitleSeparator && <div className={styles.separator} />}
            <div className={cn(styles.sidebarRight__content, contentClassName)}>{open && children}</div>
        </div>
    )
}

export default SidebarRight
